/* eslint-disable no-console -- the [Meta Connect] logs are kept from the old page; they are how failed connects get diagnosed. */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import axios from "axios";
import config from "../../../config";
import { ConversationLoader } from "../../components/OlliLogo";
import { PageLoader } from "../../components/PageLoader";
import { PrimaryButton } from "../../components/ui";
import { UndoToast } from "../../components/crm/ui";
import { TextInput } from "../../components/crm/leadsKit";
import { ConnectFrame, ConnectGate, ErrorNote, useIntegration } from "../../components/settings/integrationsKit";

/**
 * Settings → Integrations → :id → Connect Meta (design: IntegrationConnect.tsx, "meta").
 *
 * THE FLOW IS THE OLD ConnectMeta.js, UNCHANGED. Every function below (SDK
 * loading, FB.login with the same scopes / return_scopes / auth_type, the
 * scope check, the code-exchange fallback, the four-way page discovery, the
 * per-page connect with the user token, the manual connect) is copied from
 * src/pages/settings/Integrations/ConnectMeta.js. Only the markup is the design's.
 *
 * Design → real mapping:
 *   intro  → "Connect with Meta" = handleMetaLogin; manual setup = handleManualConnect
 *   waiting→ shown while the pages are being fetched after Meta returns a token
 *   page   → the pages Meta returned (multi-select, as the old page; Continue with
 *            one page picks its forms, with several connects them all)
 *   forms  → the page's real lead forms (FB /{page}/leadgen_forms); Connect =
 *            handleBulkConnect (the old "Connect page" call, with form_ids).
 */

const metaPermissions = ["Manage your business (business_management)", "Show a list of the Pages you manage", "Manage accounts, settings and webhooks for a Page", "Access lead information"];

export default function ConnectMeta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { row: integration, loading } = useIntegration(id);
  const [connecting, setConnecting] = useState(false);
  const [step, setStep] = useState(1); // 1: Connect, 2: Select Page, 3: Select Forms
  const [sdkError, setSdkError] = useState("");
  const [pagesLoading, setPagesLoading] = useState(false);

  const [metaUserToken, setMetaUserToken] = useState("");
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [forms, setForms] = useState([]);
  const [formsFor, setFormsFor] = useState(null); // the page id `forms` were loaded for
  const formsPage = useRef(null);
  const [selectedForms, setSelectedForms] = useState([]);
  const [bulkConnectProgress, setBulkConnectProgress] = useState(null);

  const [toast, setToast] = useState(null);

  const [manualMode, setManualMode] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [manualPageId, setManualPageId] = useState("");
  const [manualPageName, setManualPageName] = useState("");

  const META_APP_ID = process.env.REACT_APP_META_LEADS_APP_ID || process.env.REACT_APP_META_APP_ID || "";

  useEffect(() => {
    if (!META_APP_ID) {
      setSdkError("Meta App ID not configured. Please use Manual Setup.");
      setManualMode(true);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Old page: an integration that is already connected goes straight to its settings.
  useEffect(() => { if (integration?.is_connected) navigate(`/settings/integrations/${id}`); }, [integration, id, navigate]);

  const showToast = (message) => setToast({ message });

  // ---- Copied from the old ConnectMeta.js ----

  const initFacebookSDK = () => {
    return new Promise((resolve, reject) => {
      if (!META_APP_ID) {
        reject(new Error("Meta App ID not configured"));
        return;
      }
      if (window.FB) {
        resolve();
        return;
      }
      window.fbAsyncInit = function () {
        window.FB.init({
          appId: META_APP_ID,
          cookie: true,
          xfbml: true,
          version: "v21.0",
        });
        resolve();
      };
      if (document.getElementById("facebook-jssdk")) {
        return;
      }
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        reject(new Error("Failed to load Meta SDK"));
      };
      document.body.appendChild(script);
      setTimeout(() => {
        if (!window.FB) {
          reject(new Error("Meta SDK load timeout"));
        }
      }, 10000);
    });
  };

  const handleMetaLogin = async () => {
    if (!META_APP_ID) {
      showToast("Meta App ID not configured. Please use Manual Setup below.");
      setManualMode(true);
      return;
    }

    setConnecting(true);
    setSdkError("");

    try {
      await initFacebookSDK();

      if (!window.FB) {
        throw new Error("Meta SDK not available");
      }

      window.FB.login((response) => {
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          const authCode = response.authResponse.code;
          const grantedScopes = response.authResponse.grantedScopes || "";
          console.log("[Meta Connect] Granted scopes:", grantedScopes);

          const required = ["pages_show_list", "pages_manage_metadata", "leads_retrieval"];
          const grantedList = grantedScopes.split(",").map((s) => s.trim());
          const missing = required.filter((s) => !grantedList.includes(s));
          if (missing.length > 0 && accessToken) {
            const msg = `Missing required permissions: ${missing.join(", ")}. Please reconnect and click "Edit access" in the Facebook popup to enable all permissions.`;
            console.warn("[Meta Connect]", msg);
            setSdkError(msg);
            showToast(msg);
            setConnecting(false);
            return;
          }
          if (!grantedList.includes("business_management")) {
            console.warn("[Meta Connect] business_management not granted — BM-owned pages will not be accessible. Will still try /me/accounts.");
            showToast('Tip: pages managed via Business Manager require the "Business Management" permission. If no pages show up, click "Edit access" in the Facebook popup next time and enable it.');
          }

          if (accessToken) {
            loadPages(accessToken);
            setStep(2);
          } else if (authCode) {
            exchangeCodeForToken(authCode, window.location.origin + "/");
          } else {
            showToast("No token or code received from Facebook");
          }
        } else {
          const msg = response.status === "not_authorized"
            ? "You declined the Facebook permissions. Please retry and allow all permissions."
            : "Login was cancelled or blocked. If a popup blocker is active, allow popups for this site and retry.";
          setSdkError(msg);
          showToast(msg);
        }
        setConnecting(false);
      }, {
        scope: [
          "pages_show_list", "pages_manage_metadata", "leads_retrieval",
          "business_management", "pages_read_engagement", "pages_read_user_content",
          "read_insights",
          "ads_management", "ads_read", "pages_manage_ads", "pages_manage_posts", "pages_manage_engagement",
          "instagram_basic", "instagram_content_publish", "instagram_manage_insights", "instagram_manage_comments",
          "pages_messaging", "instagram_manage_messages",
        ].join(","),
        return_scopes: true,
        auth_type: "rerequest",
      });
    } catch (err) {
      console.error("Meta SDK Error:", err);
      setSdkError(err.message || "Failed to initialize Meta login");
      showToast("Meta SDK failed. Please use Manual Setup.");
      setManualMode(true);
      setConnecting(false);
    }
  };

  const exchangeCodeForToken = async (code, redirectUri) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${config.API_BASE_URL}/api/integrations/${id}/exchange-meta-code`,
        { code, redirect_uri: redirectUri },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success && response.data.access_token) {
        loadPages(response.data.access_token);
        setStep(2);
      } else {
        showToast(response.data.message || "Failed to exchange code");
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to exchange code for token");
    }
  };

  const fetchMetaPages = async (accessToken) => {
    try {
      setMetaUserToken(accessToken || "");

      const collectAndDedupe = (lists) => {
        const seen = new Map();
        for (const arr of lists) {
          if (!Array.isArray(arr)) continue;
          for (const p of arr) {
            if (p?.id && !seen.has(p.id)) seen.set(p.id, p);
          }
        }
        return Array.from(seen.values());
      };

      const fbApi = (path, params) => new Promise((resolve) => {
        window.FB.api(path, params || {}, (response) => resolve(response));
      });

      const [rA, rB, businesses] = await Promise.all([
        fbApi("/me/accounts", { fields: "id,name,access_token,instagram_business_account", limit: 100 }),
        fbApi("/me", { fields: "id,name,accounts.limit(100){id,name,access_token,instagram_business_account}" }),
        fbApi("/me/businesses", { fields: "id,name", limit: 50 }),
      ]);

      let bmPages = [];
      if (Array.isArray(businesses?.data) && businesses.data.length > 0) {
        const bmFetches = [];
        for (const biz of businesses.data) {
          bmFetches.push(fbApi(`/${biz.id}/owned_pages`, { fields: "id,name,access_token,instagram_business_account", limit: 100 }));
          bmFetches.push(fbApi(`/${biz.id}/client_pages`, { fields: "id,name,access_token,instagram_business_account", limit: 100 }));
        }
        const bmResults = await Promise.all(bmFetches);
        bmPages = collectAndDedupe(bmResults.map((r) => r?.data));
      }

      const allPages = collectAndDedupe([rA?.data, rB?.accounts?.data, bmPages]);
      const pagesWithToken = allPages.filter((p) => p.access_token);
      const pagesWithoutToken = allPages.filter((p) => !p.access_token);

      console.log(`[Meta Connect] Total unique pages: ${allPages.length} (${pagesWithToken.length} with token, ${pagesWithoutToken.length} without)`);

      if (pagesWithToken.length > 0) {
        setPages(pagesWithToken);
        if (pagesWithoutToken.length > 0) {
          showToast(`${pagesWithoutToken.length} more page(s) found but missing access tokens — likely need business_management permission`);
        }
        return;
      }

      if (allPages.length > 0) {
        const msg = `Found ${allPages.length} page(s) you have access to, but Facebook did not return access tokens for them. This typically means the 'business_management' permission was not granted. Reconnect and click "Edit access" in the Facebook popup to enable that permission.`;
        console.warn("[Meta Connect]", msg, allPages);
        setSdkError(msg);
        showToast(msg);
        return;
      }

      const errorParts = [rA?.error?.message, rB?.error?.message, businesses?.error?.message].filter(Boolean);
      const msg = errorParts.length > 0
        ? `Facebook errors: ${errorParts.join(" | ")}`
        : 'Facebook returned 0 pages and 0 businesses. Your account does not appear to have direct Page admin roles or Business Manager access. If you manage pages via Business Manager, ensure the "business_management" permission was granted (use "Edit access" in the Facebook popup to enable it).';
      console.warn("[Meta Connect]", msg);
      setSdkError(msg);
      showToast("No pages returned by Facebook — see the note above");
    } catch (err) {
      console.error("[Meta Connect] fetchMetaPages threw:", err);
      setSdkError(err.message || "Failed to fetch pages");
      showToast("Failed to fetch pages");
    }
  };

  /** fetchMetaPages, with the design's "Waiting for Meta" state shown while it runs. */
  const loadPages = async (accessToken) => {
    setPagesLoading(true);
    try { await fetchMetaPages(accessToken); } finally { setPagesLoading(false); }
  };

  const handleTogglePageSelection = (pageId) => {
    setSelectedPageIds((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const handleSelectAllPages = () => {
    if (selectedPageIds.size === pages.length) setSelectedPageIds(new Set());
    else setSelectedPageIds(new Set(pages.map((p) => p.id)));
  };

  const handleSelectPage = async (page) => {
    setSelectedPage(page);
    setSelectedPageIds(new Set([page.id]));
    setStep(3);
    formsPage.current = page.id;
    setForms([]);
    try {
      window.FB.api(
        `/${page.id}/leadgen_forms`,
        { access_token: page.access_token },
        (response) => {
          if (formsPage.current !== page.id) return; // another page was picked since
          if (response?.data) {
            setForms(response.data);
          }
          setFormsFor(page.id);
        },
      );
    } catch (err) {
      console.error("Failed to fetch forms:", err);
      setFormsFor(page.id);
    }
  };

  const handleFormToggle = (formId) => {
    setSelectedForms((prev) => (prev.includes(formId) ? prev.filter((x) => x !== formId) : [...prev, formId]));
  };

  const handleBulkConnect = async () => {
    const selectedPages = pages.filter((p) => selectedPageIds.has(p.id));
    if (selectedPages.length === 0) {
      showToast("Select at least one page");
      return;
    }

    setConnecting(true);
    const token = localStorage.getItem("token");
    const results = { ok: [], failed: [] };

    try {
      for (let i = 0; i < selectedPages.length; i++) {
        const page = selectedPages[i];
        setBulkConnectProgress({ done: i, total: selectedPages.length, currentPage: page.name });

        let targetId = id;
        if (i > 0) {
          try {
            const createRes = await axios.post(
              `${config.API_BASE_URL}/api/integrations`,
              { integration_type: "meta_leads", config_name: `Meta — ${page.name}` },
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (createRes.data?.success) targetId = createRes.data.data.id;
            else throw new Error(createRes.data?.message || "Failed to create integration row");
          } catch (e) {
            results.failed.push({ page: page.name, message: e.response?.data?.message || e.message });
            continue;
          }
        }

        try {
          await axios.post(
            `${config.API_BASE_URL}/api/integrations/${targetId}/connect-meta`,
            {
              access_token: page.access_token,
              user_access_token: metaUserToken || undefined,
              page_id: page.id,
              page_name: page.name,
              form_ids: i === 0 ? selectedForms : [],
              instagram_business_id: page.instagram_business_account?.id,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          results.ok.push({ page: page.name, integrationId: targetId });
        } catch (e) {
          if (e.response?.data?.requires_action) {
            results.failed.push({ page: page.name, message: e.response.data.message, requiresAction: true });
          } else {
            results.failed.push({ page: page.name, message: e.response?.data?.message || e.message });
          }
        }
      }
    } finally {
      setBulkConnectProgress(null);
      setConnecting(false);
    }

    if (results.ok.length === 1 && results.failed.length === 0) {
      showToast("Page connected successfully!");
      setTimeout(() => navigate(`/settings/integrations/${results.ok[0].integrationId}`, { state: { connected: "Meta" } }), 1500);
    } else if (results.ok.length > 1 && results.failed.length === 0) {
      showToast(`Connected ${results.ok.length} pages successfully!`);
      setTimeout(() => navigate("/settings/integrations"), 1500);
    } else if (results.ok.length > 0 && results.failed.length > 0) {
      const errorSummary = results.failed.map((f) => `${f.page}: ${f.message}`).join(" | ");
      setSdkError(`${results.ok.length} of ${selectedPages.length} pages connected. Failures: ${errorSummary}`);
      showToast(`Connected ${results.ok.length} of ${selectedPages.length} pages — see the note above`);
    } else {
      const errorSummary = results.failed.map((f) => `${f.page}: ${f.message}`).join(" | ");
      setSdkError(errorSummary);
      showToast("No pages were connected — see the note above");
    }
  };

  const handleManualConnect = async () => {
    if (!manualToken || !manualPageId) {
      showToast("Please fill all required fields");
      return;
    }
    setConnecting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${config.API_BASE_URL}/api/integrations/${id}/connect-meta`,
        { access_token: manualToken, page_id: manualPageId, page_name: manualPageName || "Meta Page", form_ids: [] },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        showToast("Successfully connected!");
        setTimeout(() => navigate(`/settings/integrations/${id}`, { state: { connected: "Meta" } }), 1500);
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to connect";
      if (err.response?.data?.requires_action) {
        setSdkError(message);
        showToast("Connection rejected by Meta — see the note above");
      } else {
        showToast(message);
      }
    } finally {
      setConnecting(false);
    }
  };

  // ---- Design markup ----

  const onePage = selectedPageIds.size === 1;
  const continueFromPages = () => {
    if (onePage) { const page = pages.find((p) => p.id === [...selectedPageIds][0]); if (page) handleSelectPage(page); }
    else handleBulkConnect();
  };

  return <ConnectGate loading={loading} row={integration}>
    <ConnectFrame monogram="ME" title="Connect Meta (Facebook + Instagram)" description="Capture new enquiries from Facebook and Instagram Lead Ads.">
      <div className="mt-4 flex justify-center gap-2"><span className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium">Facebook lead ads</span><span className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium">Instagram lead ads</span></div>
      <ErrorNote>{sdkError}</ErrorNote>
      {step === 1 && <><div className="mt-5 rounded-md bg-mist p-4"><h2 className="text-small font-semibold">How it works</h2><ol className="mt-2 list-inside list-decimal space-y-1.5 text-small text-muted"><li>Connect with Meta to authorise access</li><li>Choose the page you want to connect</li><li>Choose which lead forms to sync</li><li>Leads from Facebook and Instagram arrive automatically</li></ol></div><div className="mt-4 rounded-md bg-warning-soft p-4 text-warning"><div className="flex items-center gap-2 text-small font-semibold"><TriangleAlert size={17} strokeWidth={1.75} />Important: turn on all permissions</div><p className="mt-2 text-caption leading-relaxed">In the Facebook popup, choose “Edit access” and turn on every permission, especially:</p><ul className="mt-2 list-inside list-disc space-y-1 text-caption">{metaPermissions.map((p) => <li key={p}>{p}</li>)}</ul><p className="mt-2 text-caption">Without these toggled on, Facebook may return no pages even if you are an admin.</p></div><PrimaryButton className="mt-5 w-full" disabled={!META_APP_ID} loading={connecting} onClick={handleMetaLogin}>Connect with Meta</PrimaryButton><button type="button" onClick={() => setManualMode(!manualMode)} className="mt-3 w-full py-2 text-caption font-medium text-muted hover:text-ink">{manualMode ? "Hide manual setup" : "Manual setup (advanced)"}</button>{manualMode && <div className="mt-2 rounded-md bg-mist p-4"><h3 className="text-small font-semibold">Manual configuration</h3><p className="mt-1 text-caption text-muted">For testing or when Meta login is not available.</p><div className="mt-4 grid gap-3"><label className="text-caption font-semibold">Page access token *<TextInput value={manualToken} onChange={(e) => setManualToken(e.target.value)} placeholder="Enter your page access token" className="mt-1" /></label><label className="text-caption font-semibold">Page ID *<TextInput value={manualPageId} onChange={(e) => setManualPageId(e.target.value)} placeholder="Enter your Facebook or Instagram Page ID" className="mt-1" /></label><label className="text-caption font-semibold">Page name (optional)<TextInput value={manualPageName} onChange={(e) => setManualPageName(e.target.value)} placeholder="Enter page name" className="mt-1" /></label><button type="button" disabled={!manualToken || !manualPageId || connecting} onClick={handleManualConnect} className="w-fit rounded-full px-4 py-2 text-small font-semibold ring-1 ring-line hover:bg-paper disabled:opacity-50">Connect manually</button></div></div>}</>}
      {step === 2 && pagesLoading && <div className="flex flex-col items-center py-12"><ConversationLoader size={42} tone="black" /><p className="mt-4 text-small font-semibold">Waiting for Meta</p><p className="mt-1 text-caption text-muted">Authorising access to your pages</p></div>}
      {step === 2 && !pagesLoading && <div className="mt-6"><div className="flex items-center justify-between gap-3"><h2 className="text-small font-semibold">Choose a page</h2>{pages.length > 1 && <button type="button" onClick={handleSelectAllPages} className="text-caption font-semibold text-sky-deep">{selectedPageIds.size === pages.length ? "Clear all" : "Select all"}</button>}</div><p className="mt-1 text-caption text-muted">Pick one or more. Each page is connected as a separate integration.</p>{pages.length > 0 && <div className="mt-3 flex flex-col divide-y divide-line rounded-md border border-line">{pages.map((page) => <label key={page.id} className="flex cursor-pointer items-start gap-3 px-4 py-3"><input type="checkbox" checked={selectedPageIds.has(page.id)} onChange={() => handleTogglePageSelection(page.id)} className="mt-1 accent-ink" /><span className="flex-1"><span className="block text-small font-medium">{page.name}</span><span className="text-caption text-muted">Page ID {page.id}{page.instagram_business_account?.id ? " · Instagram linked" : ""}</span></span></label>)}</div>}{bulkConnectProgress && <p className="mt-3 text-caption text-muted">Connecting {bulkConnectProgress.currentPage}… {bulkConnectProgress.done} / {bulkConnectProgress.total}</p>}<PrimaryButton className="mt-5 w-full" disabled={selectedPageIds.size === 0} loading={connecting} onClick={continueFromPages}>{selectedPageIds.size > 1 ? `Connect ${selectedPageIds.size} pages` : "Continue"}</PrimaryButton></div>}
      {step === 3 && <div className="mt-6"><h2 className="text-small font-semibold">Choose lead forms</h2><p className="mt-1 text-caption text-muted">Optional. You can connect {selectedPage?.name || "this page"} without choosing a form.</p>{formsFor !== selectedPage?.id ? <PageLoader label="Loading lead forms…" className="py-10" /> : forms.length > 0 && <div className="mt-3 divide-y divide-line rounded-md border border-line">{forms.map((form) => <label key={form.id} className="flex cursor-pointer items-center gap-3 px-4 py-3"><input type="checkbox" checked={selectedForms.includes(form.id)} onChange={() => handleFormToggle(form.id)} className="accent-ink" /><span className="flex-1 text-small">{form.name}</span><span className="text-caption capitalize text-muted">{form.status}</span></label>)}</div>}<PrimaryButton className="mt-5 w-full" loading={connecting} onClick={handleBulkConnect}>Connect</PrimaryButton></div>}
    </ConnectFrame>
    {toast && <UndoToast message={toast.message} onDone={() => setToast(null)} />}
  </ConnectGate>;
}
